/**
 * W6 — the pure rules behind the call log.
 *
 * `GET /api/calls` already decided what a call *was*: the chat it happened in, the direction
 * relative to the viewer, `missed` (ended with nobody but the caller ever in the room — an
 * unanswered ring and a decline alike) and `durationMs` (measured from the answer, §4), newest
 * first. What is left for the client is the row's one state line and whether the row may ring the
 * chat again — pure, pinned by `calls.test.ts`, and never re-derived at a call site (the rule
 * `lib/search.ts` and `lib/chat.ts` follow).
 */
import type { CallLogView, ChatView } from '@olmt/shared'
import type { MessageKey } from './i18n'
import { formatDuration } from './time'

/** A row's state line: a dictionary key plus its placeholders, never a sentence. */
export type CallLogSummary = { key: MessageKey; vars?: Record<string, string | number> }

/**
 * What the state line under a call says. `missed` is the api's derivation and outranks everything:
 * a call that ended with nobody but the caller ever in the room reads so whichever side placed it,
 * and the row must not re-derive it back into a direction. A duration is next — it exists only for
 * a call someone answered, and it is a compound, so it goes through `formatDuration` and arrives
 * isolated. A call with neither (still running, or ended before the api kept reasons) is its
 * direction.
 */
export function callLogSummary(entry: CallLogView): CallLogSummary {
  if (entry.missed) return { key: 'calls.missed' }
  if (entry.durationMs !== null) {
    return { key: 'calls.duration', vars: { duration: formatDuration(entry.durationMs) } }
  }
  return { key: entry.direction === 'outgoing' ? 'calls.outgoing' : 'calls.incoming' }
}

/**
 * Whether the row may ring that chat again. The log's copy of the chat is from the read; this asks
 * the *live* one (`useChatStore`), because starting a call needs membership the api will check
 * (§4): a chat the viewer has left since — leaving drops it from their list — loses the button
 * rather than posting a ring the server would refuse. A chat the store has not heard of yet, or
 * one whose member list no longer holds the viewer, is the same answer: not callable.
 */
export function canCallBack(
  entry: CallLogView,
  chat: ChatView | null | undefined,
  meId: string | null,
): boolean {
  if (chat == null || chat.id !== entry.chat.id || meId === null) return false
  return chat.members.some((member) => member.id === meId)
}
